import { useEffect, useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { gameHubTabsBySupportState, type Game, type TrophySet } from '@plathunt/types';
import { useApi } from '../app/providers.js';
import { SetSwitcher } from '../components/SetSwitcher.js';
import { TrophyCountBar } from '../components/TrophyCountBar.js';

const tabLabels: Record<string, string> = {
  overview: 'Übersicht',
  trophies: 'Trophäen',
  roadmap: 'Roadmap',
  completion: '100 %',
  more: 'Mehr',
};

const tabPaths: Record<string, string> = {
  overview: '',
  trophies: 'trophies',
  roadmap: 'roadmap',
  completion: 'completion',
  more: 'more',
};

/** Was der Outlet an die Tabs weiterreicht. Game und Set bleiben getrennt. */
export interface GameHubContext {
  game: Game | null;
  set: TrophySet;
  sets: TrophySet[];
}

interface HubData {
  game: Game | null;
  set: TrophySet | null;
  sets: TrophySet[];
}

/**
 * Der Support State entscheidet, welche Tabs es gibt.
 * basic zeigt Roadmap und 100 % nicht als leere oder deaktivierte Tabs –
 * sie existieren einfach nicht. catalog oeffnet gar keinen Hub.
 */
export function GameHub() {
  const { gameId, trophySetId } = useParams();
  const api = useApi();

  const [data, setData] = useState<HubData>({ game: null, set: null, sets: [] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!trophySetId || !gameId) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    // Das Set ist Pflicht. Game und Set-Liste sind Beiwerk fuer Kopf und
    // Versionswechsel – wenn sie fehlen, bleibt der Hub trotzdem benutzbar.
    Promise.all([
      api.trophies.getTrophySet(trophySetId),
      api.games.getGameById(gameId).catch(() => null),
      api.games.getGameTrophySets(gameId).catch(() => [] as TrophySet[]),
    ])
      .then(([set, game, sets]) => {
        if (!cancelled) setData({ set, game, sets });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, gameId, trophySetId]);

  if (loading) {
    return (
      <section aria-busy="true">
        <div className="ph-skeleton ph-skeleton--title" />
        <div className="ph-skeleton ph-skeleton--line" />
        <div className="ph-skeleton ph-skeleton--block" />
      </section>
    );
  }

  if (failed) {
    return (
      <section>
        <h1 className="ph-screen__title">Konnte nicht geladen werden</h1>
        <p className="ph-screen__note">
          Die Trophäendaten sind gerade nicht erreichbar. Bitte später noch einmal versuchen.
        </p>
      </section>
    );
  }

  const { set, game, sets } = data;

  if (!set) {
    return (
      <section>
        <h1 className="ph-screen__title">Trophy Set noch nicht verfügbar</h1>
        <p className="ph-screen__note">
          Für dieses Spiel ist noch kein Trophy Set entdeckt. Es wird kein Hub geöffnet und keine
          Trophäendaten vorgetäuscht.
        </p>
      </section>
    );
  }

  const tabs = gameHubTabsBySupportState[set.supportState];
  const base = `/game/${gameId}/set/${trophySetId}`;
  const context: GameHubContext = { game, set, sets };

  return (
    <section>
      <header className="ph-gamehead">
        {set.trophyTitleIconUrl ? (
          <img className="ph-gamehead__art" src={set.trophyTitleIconUrl} alt="" loading="lazy" />
        ) : game?.coverUrl ? (
          <img className="ph-gamehead__art" src={game.coverUrl} alt="" loading="lazy" />
        ) : (
          <div className="ph-gamehead__art ph-gamehead__art--empty" aria-hidden="true" />
        )}

        <div className="ph-gamehead__text">
          {/* Game-Identitaet oben, konkrete Version darunter. */}
          <p className="ph-gamehead__game">{game?.title ?? 'Spiel'}</p>
          <h1 className="ph-gamehead__title">{set.title}</h1>
          <p className="ph-gamehead__meta">
            <span className="ph-chip">{set.platform.toUpperCase()}</span>
            <span className="ph-chip">{set.region.toUpperCase()}</span>
            {set.version && <span className="ph-chip">v{set.version}</span>}
            <span className="ph-chip">
              {set.trophyCount.total} {set.trophyCount.total === 1 ? 'Trophäe' : 'Trophäen'}
            </span>
            {set.hasPlatinum && <span className="ph-chip ph-chip--platinum">Platin</span>}
          </p>
        </div>
      </header>

      {gameId && <SetSwitcher gameId={gameId} sets={sets} activeSetId={set.id} />}

      <TrophyCountBar counts={set.trophyCount} hasPlatinum={set.hasPlatinum} />

      <nav className="ph-hubtabs" aria-label="Bereiche">
        {tabs.map((tab) => (
          <NavLink
            key={tab}
            end={tab === 'overview'}
            to={tabPaths[tab] ? `${base}/${tabPaths[tab]}` : base}
            className={({ isActive }) => `ph-hubtabs__item${isActive ? ' is-active' : ''}`}
          >
            {tabLabels[tab]}
          </NavLink>
        ))}
      </nav>

      <Outlet context={context} />
    </section>
  );
}
